import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useContainerSize } from "@/core/hooks/useContainerSize";
import { tailleAffichageAtom } from "@/core/state";
import {
  contexteEcran,
  normaliserTaille,
  resoudreTaille,
  TAILLES_AFFICHAGE,
} from "@/core/theme/tailleAffichage";
import { Onglets, Select } from "@/shared/ui";

/**
 * Sélecteur de taille d'affichage — partagé, comme `WallpaperPicker`, entre
 * les Préférences (Paramètres) et la modale du rail gauche. Un seul rendu,
 * donc un seul endroit à corriger : c'est ce qui a manqué au fond d'écran
 * avant qu'il ne soit sorti en composant.
 *
 * La mécanique (pourquoi `zoom` sur `<body>`, pourquoi « auto » se calcule sur
 * `devicePixelRatio`) vit dans `core/theme/tailleAffichage.ts`.
 *
 * Dessin (coque, 01-11-2026) : un seul groupe de segments (`Onglets`, la
 * sémantique d'un choix de valeur) — le cran choisi est la pastille qui glisse,
 * plus d'anneau gris autour de chaque bouton. Sept segments ne tiennent pas
 * dans les 300 px du panneau « Personnaliser » (ils défileraient, et 110 / 125 %
 * resteraient cachés) : en dessous de 440 px de large, le même choix est un
 * `Select`.
 */
export function TailleAffichagePicker({
  /** Le rappel « ce réglage suit cet appareil ». Masqué là où la modale le dit
   *  déjà dans son propre sous-titre. */
  avecRappel = true,
}: {
  avecRappel?: boolean;
} = {}) {
  const { t } = useTranslation();
  const [taille, setTaille] = useAtom(tailleAffichageAtom);
  const courant = normaliserTaille(taille);

  // Ce que « Auto » donne SUR CET ÉCRAN. Un « Auto » qui n'annonce pas son
  // résultat est une boîte noire : la personne qui trouve son cockpit trop
  // petit doit pouvoir lire le chiffre et le reprendre à la main.
  // `resize` est le signal : Chrome l'émet aussi quand le zoom navigateur
  // change, or c'est lui qui bouge `devicePixelRatio`.
  const [ecran, setEcran] = useState(contexteEcran);
  useEffect(() => {
    const surResize = () => setEcran(contexteEcran());
    window.addEventListener("resize", surResize);
    return () => window.removeEventListener("resize", surResize);
  }, []);
  const effectif = resoudreTaille(taille, ecran);

  const { ref, size } = useContainerSize<HTMLDivElement>();
  const etroit = size.width > 0 && size.width < 440;
  const nom = t("shell.settings.preferences.tailleAffichage.title", {
    defaultValue: "Taille de l'affichage",
  });
  const valeur = String(courant);
  const choisir = (v: string) => setTaille(v === "auto" ? "auto" : Number(v));

  const options = [
    {
      valeur: "auto",
      etiquette: t("shell.settings.preferences.tailleAffichage.auto", {
        defaultValue: "Auto — {{pourcent}} %",
        pourcent: effectif,
      }),
    },
    ...TAILLES_AFFICHAGE.map((cran) => ({ valeur: String(cran), etiquette: `${cran} %` })),
  ];

  return (
    <div ref={ref} className="flex flex-col gap-3">
      {etroit ? (
        <Select aria-label={nom} value={valeur} onChange={(e) => choisir(e.target.value)}>
          {options.map((o) => (
            <option key={o.valeur} value={o.valeur}>
              {o.etiquette}
            </option>
          ))}
        </Select>
      ) : (
        <Onglets
          style="segments"
          semantique="choix"
          taille="sm"
          nom={nom}
          options={options}
          valeur={valeur}
          onChange={choisir}
        />
      )}
      {avecRappel && (
        <p className="text-[12px] leading-relaxed text-faint">
          {t("shell.settings.preferences.tailleAffichage.hint", {
            defaultValue:
              "Ce réglage suit CET appareil, pas ton compte : c'est l'écran qu'on règle. Sur un poste où Windows est à 125 %, « Auto » retrouve exactement ce que voit un écran réglé à 100 %.",
          })}
        </p>
      )}
    </div>
  );
}
